import { Link } from 'react-router-dom';
import { Chrome, Smartphone } from 'lucide-react';
import { useWebsiteContentOptional } from '../../contexts/WebsiteContentContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { CHROME_EXTENSION_URL, IOS_APP_URL, ANDROID_APP_URL } from '../../constants/downloads';

type NavLink = {
  name: string;
  href: string;
  onClick?: () => void;
  icon?: React.ComponentType<{ className?: string }>;
};

export default function Footer() {
  const currentYear = new Date().getFullYear();
  const content = useWebsiteContentOptional();
  const { t } = useLanguage();

  // Helper to get translated content with fallback
  const get = (key: string, fallback: string) =>
    content?.getContent('footer', key, fallback) ?? fallback;

  const footerLinks = {
    product: [
      { name: get('nav_features', 'Features'), href: '/#features' },
      { name: get('nav_pricing', 'Pricing'), href: '/#pricing' },
      { name: get('nav_faq', 'FAQ'), href: '/#faq' },
    ],
    company: [
      { name: get('nav_support', 'Support'), href: '#', onClick: () => window.Axiss?.open('contact') },
      { name: get('nav_privacy', 'Privacy Policy'), href: '/privacy' },
      { name: get('nav_terms', 'Terms of Service'), href: '/terms' },
      { name: get('nav_credits', 'Credits'), href: '/credits' },
    ],
    download: [
      { name: get('chrome_extension', 'Chrome Extension'), href: CHROME_EXTENSION_URL, icon: Chrome },
      { name: get('ios_app', 'iOS App'), href: IOS_APP_URL, icon: Smartphone },
      { name: get('android_app', 'Android App'), href: ANDROID_APP_URL, icon: Smartphone },
    ],
  };

  return (
    <footer className="bg-gray-900 text-gray-300" role="contentinfo">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          {/* Brand */}
          <div className="col-span-1">
            <Link to="/" className="flex items-center">
              <img src="/logo-light.svg" alt="RUMIO" className="h-8" />
            </Link>
            <p className="mt-4 text-sm text-gray-400">
              {get('description', 'Track IKEA product availability and prices worldwide. Never miss a deal again.')}
            </p>
          </div>

          {/* Product links */}
          <div>
            <h3 className="text-sm font-semibold text-white uppercase tracking-wider">
              {get('section_product', 'Product')}
            </h3>
            <ul className="mt-4 space-y-3">
              {footerLinks.product.map((link) => (
                <li key={link.name}>
                  <a
                    href={link.href}
                    className="text-sm text-gray-400 hover:text-white transition-colors"
                  >
                    {link.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Company links */}
          <div>
            <h3 className="text-sm font-semibold text-white uppercase tracking-wider">
              {get('section_company', 'Company')}
            </h3>
            <ul className="mt-4 space-y-3">
              {footerLinks.company.map((link) => (
                <li key={link.name}>
                  {'onClick' in link ? (
                    <button
                      onClick={(link as NavLink).onClick}
                      className="text-sm text-gray-400 hover:text-white transition-colors"
                    >
                      {link.name}
                    </button>
                  ) : (
                    <Link
                      to={link.href}
                      className="text-sm text-gray-400 hover:text-white transition-colors"
                    >
                      {link.name}
                    </Link>
                  )}
                </li>
              ))}
            </ul>
          </div>

          {/* Download links */}
          <div>
            <h3 className="text-sm font-semibold text-white uppercase tracking-wider">
              {get('section_download', 'Download')}
            </h3>
            <ul className="mt-4 space-y-3">
              {footerLinks.download.map((link) => (
                <li key={link.name}>
                  <a
                    href={link.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-sm text-gray-400 hover:text-white transition-colors"
                  >
                    <link.icon className="w-4 h-4" />
                    {link.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="mt-12 pt-8 border-t border-gray-800">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-sm text-gray-400">
              &copy; {currentYear} {t('common.brand', 'RUMIO')}. {get('rights_reserved', 'All rights reserved.')}
            </p>
            <p className="text-xs text-gray-500">
              {get('disclaimer', 'RUMIO is not affiliated with IKEA. IKEA is a registered trademark of Inter IKEA Systems B.V.')}
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
}
